
export function doesUserTryToOpenInNewTabOrNewWindow (event) {
  // according to https://stackoverflow.com/a/20087506
  return event.ctrlKey // new tab
    || event.shiftKey // new window
    || event.metaKey // apple
    || (event.button && event.button === 1); // middle click
}

export function noModifierKeys (event) {
  return !event.altKey && !(event.metaKey || event.ctrlKey) && !event.shiftKey;
}

/**
 * Detects if a given event was a click event generated from a keyboard 'Enter'/'Space' press and not a mouse click event.
 *
 * @param {UIEvent} event
 * @return {boolean} true if event was triggered by a keyboard enter/space press
 */
export function isKeyboardEnterClickEvent (event) {
  // see https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
  return event.detail === 0;
}

export function stopPropagationAndPreventDefault (event) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * mouse button presses, key pressed, or touches shorter than this threshold will be interpreted as
 * click and longer ones as holding.
 */
const HOLD_THRESHOLD_IN_MS = 300;

/**
 * Register different click/key/touch event handlers on the specified element depending on the length of
 * the click or touch.
 *
 * @param element - the element to add the listeners to
 * @param clickHandler - listener called on a short click or key press or touch (shorter than HOLD_THRESHOLD_IN_MS)
 * @param holdStartHandler - listener called at the beginning of a long click/touch
 * @param holdEndHandler - listener called at the end of a long click/touch
 */
export function setClickAndHoldHandlers (element, clickHandler, holdStartHandler, holdEndHandler) {
  element.onmousedown = () => {
    element.mouseOrTouchOrKeyStartTime = Date.now();
    element.touchOrClickTimeout = setTimeout(() => {
      holdStartHandler();
      element.holding = true;
    }, HOLD_THRESHOLD_IN_MS);
  };
  element.ontouchstart = e => {
    if (changedTargetTouches(e).length === 1) { // multiple touches at once on same button not supported
      element.touchId = e.changedTouches.item(0).identifier;
      element.onmousedown(e);
    }
  };
  element.onkeydown = e => {
    // suppress automatic repeated keydown events when the key gets pressed long
    if (element.mouseOrTouchOrKeyStartTime != null) {
      return;
    }
    if (e.key === ' ' && document.body.classList.contains('is-keynav') && document.activeElement === element) {
      element.onmousedown();
    }
  };
  element.onmouseup = () => {
    if (element.mouseOrTouchOrKeyStartTime != null) {
      clearTimeout(element.touchOrClickTimeout);
      if (Date.now() - element.mouseOrTouchOrKeyStartTime < HOLD_THRESHOLD_IN_MS) {
        clickHandler();
      } else if (element.holding) {
        // The check of the "holding" flag is necessary to prevent the holdEndHandler from being
        // executed despite holdStartHandler never having been executed, in the following, unlikely
        // but not impossible, edge case:
        // - The elapsed time is slightly under HOLD_THRESHOLD_IN_MS at the very beginning of the
        //   execution of this function, meaning that the startHoldHandler has not been called yet.
        // - The elapsed time is slightly above HOLD_THRESHOLD_IN_MS in the if-clause that follows,
        //   leading to this else clause here being executed.
        holdEndHandler();
        element.holding = false;
      }
      element.mouseOrTouchOrKeyStartTime = null;
    }
  };
  element.onmouseleave = element.onmouseup;
  element.ontouchend = e => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const endedTargetTouch = e.changedTouches.item(i);
      if (endedTargetTouch.identifier === element.touchId) {
        element.touchId = null;
        element.onmouseup(e);
        e.preventDefault(); // prevent additional firing of mouseup event
      }
    }
  };
  element.onkeyup = e => {
    if (e.key === ' ' && document.body.classList.contains('is-keynav') && document.activeElement === element) {
      element.onmouseup();
    }
  };

  // prevent context menu from appearing on long press
  element.oncontextmenu = () => {
    stopPropagationAndPreventDefault(event);
    return false;
  };
}

function changedTargetTouches (touchEvent) {
  const changedTargetTouches = [];
  for (let i = 0; i < touchEvent.changedTouches.length; i++) {
    const changedTouch = touchEvent.changedTouches.item(i);
    for (let j = 0; j < touchEvent.targetTouches.length; j++) {
      const targetTouch = touchEvent.targetTouches.item(j);
      if (changedTouch.identifier === targetTouch.identifier) {
        changedTargetTouches.push(changedTouch);
      }
    }
  }
  return changedTargetTouches;
}

/**
 * Register handlers for different kind of key presses for a list of keys. The listeners only apply
 * to key presses which started without any modifier keys being pressed.
 *
 *  @param element - the element the event handlers are applied to
 *  @param keys - array of keys which are considered in the event handlers
 *  @param keyDownHandler - called immediately when one of the specified keys is pressed down
 *  @param shortPressHandler - called when the key is released, if it is released after less than HOLD_THRESHOLD_IN_MS
 *  @param holdStartHandler - called when the key is pressed down for HOLD_THRESHOLD_IN_MS
 *  @param holdEndHandler - called when the key is released after having been pressed for at least HOLD_THRESHOLD_IN_MS
 *  @param stopPropagation - (default = true) determines if event propagation should be stopped for the keys handled here (only if no modifier key is pressed)
 *  @param preventDefault - (default = true) determines if the execution of the browser default handler should be prevented for the keys handled here (only if no modifier key is pressed)
 */
export function setKeyHandlers (element, keys, keyDownHandler, shortPressHandler, holdStartHandler, holdEndHandler, stopPropagation = true, preventDefault = true) {
  element.keyPressStartTimes = element.keyPressStartTimes || new Map();
  element.keyPressTimeouts = element.keyPressTimeouts || new Map();
  element.keyHoldingFlags = element.keyHoldingFlags || new Map();

  element.addEventListener('keydown', event => {
    if (keys.includes(event.key) && noModifierKeys(event)) {
      if (stopPropagation) {
        event.stopPropagation();
      }
      if (preventDefault) {
        event.preventDefault();
      }

      // suppress automatic repeated keydown events when the key gets pressed long
      if (element.keyPressStartTimes.has(event.key)) {
        return;
      }

      if (keyDownHandler != null) {
        keyDownHandler();
      }
      element.keyPressStartTimes.set(event.key, Date.now());
      element.keyPressTimeouts.set(event.key, setTimeout(() => {
        element.keyHoldingFlags.set(event.key, true);
        if (holdStartHandler != null) {
          holdStartHandler();
        }
      }, HOLD_THRESHOLD_IN_MS));
    }
  });

  element.addEventListener('keypress', event => {
    if (keys.includes(event.key) && noModifierKeys(event)) {
      if (stopPropagation) {
        event.stopPropagation();
      }
      if (preventDefault) {
        event.preventDefault();
      }
    }
  });

  element.addEventListener('keyup', event => {
    if (keys.includes(event.key) && element.keyPressStartTimes.has(event.key)) {
      if (stopPropagation) {
        event.stopPropagation();
      }
      if (preventDefault) {
        event.preventDefault();
      }

      clearTimeout(element.keyPressTimeouts.get(event.key));
      if (Date.now() - element.keyPressStartTimes.get(event.key) < HOLD_THRESHOLD_IN_MS) {
        if (shortPressHandler != null) {
          shortPressHandler();
        }
      } else if (element.keyHoldingFlags.get(event.key)) {
        if (holdEndHandler != null) {
          holdEndHandler();
        }
        element.keyHoldingFlags.set(event.key, false);
      }
      element.keyPressStartTimes.delete(event.key);
    }
  });
}
